import { IdeaIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import { PublicSectionGate } from '@components/PublicPage'
import { EmptyState } from '@components/TripOverview'
import { usePublicIdeas, usePublicOverview } from '@hooks'
import { EventCategory, GetIdeaResponse } from '@orbit/api-types'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import PublicIdeaModal from './PublicIdeaModal'
import { buildTravelerLookup, travelerFullName } from './publicMembers'
import { filterIdeas, getCategoryOptions, getLocationOptions, ideaThumbnailUrl } from './publicIdeas'
import {
	FilterRow,
	FilterSelect,
	IdeaName,
	IdeasGrid,
	IdeaThumbnail,
	IdeaTile,
} from './PublicIdeasPage.styles'
import PublicSectionLayout from './PublicSectionLayout'

/**
 * Public, read-only ideas at `/shared/trips/:tripCode/ideas`. Unlike the member
 * board (columns by category), this renders one flat grid of square tiles —
 * link-preview thumbnail with the idea name below, the same tile language as
 * iOS's ideas collection and the public files grid. A location dropdown at the
 * top narrows the grid; options and matching mirror iOS (locality, falling
 * back to the free-typed location text). Gating (loading / 404-unavailable /
 * transient error) is the shared `PublicSectionGate`.
 */
const PublicIdeasPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const query = usePublicIdeas(tripCode)
	const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
	const [selectedCategory, setSelectedCategory] = useState<EventCategory | null>(null)
	const [viewerIdea, setViewerIdea] = useState<GetIdeaResponse | null>(null)

	// Creator names resolve from the overview's travelers (already cached from
	// the layout's breadcrumb query); until it lands the popup omits "Added by".
	const { data: overview } = usePublicOverview(tripCode)
	const travelerLookup = buildTravelerLookup(overview?.travelers ?? [])

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading ideas…"
			unavailableTitle="Ideas Unavailable"
			unavailableMessage="These ideas don't exist or haven't been shared publicly by the trip owner."
		>
			{data => {
				const ideas = data.ideas
				const locationOptions = getLocationOptions(ideas)
				const categoryOptions = getCategoryOptions(ideas)
				const visibleIdeas = filterIdeas(ideas, {
					location: selectedLocation,
					category: selectedCategory,
				})
				return (
					<PublicSectionLayout
						// Data present implies the query ran, which implies the code param existed.
						tripCode={tripCode ?? ''}
						title="Ideas"
						subtitle={`${countLabel(ideas.length, 'idea')}`}
					>
						{ideas.length === 0 ? (
							<EmptyState
								icon={<IdeaIcon sx={{ fontSize: 32 }} />}
								text="No ideas have been added to this trip yet."
							/>
						) : (
							<>
								<FilterRow>
									<FilterSelect
										aria-label="Filter by category"
										value={selectedCategory ?? ''}
										onChange={e =>
											setSelectedCategory((e.target.value || null) as EventCategory | null)
										}
									>
										<option value="">All categories</option>
										{categoryOptions.map(category => (
											<option key={category.value} value={category.value}>
												{category.label}
											</option>
										))}
									</FilterSelect>
									{locationOptions.length > 0 && (
										<FilterSelect
											aria-label="Filter by location"
											value={selectedLocation ?? ''}
											onChange={e => setSelectedLocation(e.target.value || null)}
										>
											<option value="">All locations</option>
											{locationOptions.map(location => (
												<option key={location} value={location}>
													{location}
												</option>
											))}
										</FilterSelect>
									)}
								</FilterRow>
								<IdeasGrid>
									{visibleIdeas.map((idea, index) => {
										const thumbnail = ideaThumbnailUrl(idea)
										return (
											<IdeaTile
												key={idea.id}
												type="button"
												$index={index}
												onClick={() => setViewerIdea(idea)}
											>
												<IdeaThumbnail>
													{thumbnail ? (
														<img src={thumbnail} alt={idea.name} loading="lazy" />
													) : (
														<IdeaIcon sx={{ fontSize: 40 }} />
													)}
												</IdeaThumbnail>
												<IdeaName title={idea.name}>{idea.name}</IdeaName>
											</IdeaTile>
										)
									})}
								</IdeasGrid>
							</>
						)}

						<PublicIdeaModal
							idea={viewerIdea}
							addedBy={
								viewerIdea
									? travelerFullName(travelerLookup(viewerIdea.createdByUserId))
									: null
							}
							onClose={() => setViewerIdea(null)}
						/>
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicIdeasPage
